import type { SpecialEffect, Element } from "./types.ts";
import type { Enemy } from "./Enemy.ts";

const TRAIL_LENGTH = 5;

export class Projectile {
  x = 0;
  y = 0;
  targetX = 0;
  targetY = 0;
  speed = 0;
  damage = 0;
  element: Element = "fire";
  color = "#fff";
  target: Enemy | null = null;
  /** Snapshot of target.version at fire time. Lets us detect pool recycling
   *  — if the Enemy slot was released and re-acquired, version no longer
   *  matches and we must stop homing onto the stale reference. */
  targetVersion = 0;
  active = false;
  radius = 4;
  special: SpecialEffect | null = null;
  lobbed = false;
  arcHeight = 0;
  startX = 0;
  startY = 0;
  pierceCount = 0;
  level = 1;
  hitEnemyVersions: Set<number> = new Set();
  trail: Array<{ x: number; y: number }> = [];
  private trailIdx = 0;

  init(
    x: number,
    y: number,
    target: Enemy,
    speed: number,
    damage: number,
    element: Element,
    color: string,
    special?: SpecialEffect | null,
  ): this {
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.target = target;
    this.targetVersion = target.version;
    this.targetX = target.x;
    this.targetY = target.y;
    this.speed = speed;
    this.damage = damage;
    this.element = element;
    this.color = color;
    this.active = true;
    this.special = special ?? null;
    this.lobbed = false;
    this.arcHeight = 0;
    this.pierceCount = 0;
    this.hitEnemyVersions.clear();
    return this;
  }

  initLob(
    x: number,
    y: number,
    targetX: number,
    targetY: number,
    speed: number,
    damage: number,
    element: Element,
    color: string,
    special: SpecialEffect | null,
    arcHeight: number,
    level = 1,
  ): this {
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.target = null;
    this.targetVersion = 0;
    this.targetX = targetX;
    this.targetY = targetY;
    this.speed = speed;
    this.damage = damage;
    this.element = element;
    this.color = color;
    this.active = true;
    this.special = special ?? null;
    this.pierceCount = 0;
    this.level = level;
    this.hitEnemyVersions.clear();
    this.lobbed = true;
    this.arcHeight = arcHeight;
    return this;
  }

  reset(): void {
    this.active = false;
    this.target = null;
    this.targetVersion = 0;
    this.special = null;
    this.lobbed = false;
    this.arcHeight = 0;
    this.startX = 0;
    this.startY = 0;
    this.pierceCount = 0;
    this.level = 1;
    this.hitEnemyVersions.clear();
    this.trail.length = 0;
    this.trailIdx = 0;
  }

  /** True iff target still refers to the same logical enemy we fired at. */
  hasLiveTarget(): boolean {
    return (
      this.target !== null &&
      this.target.active &&
      this.target.version === this.targetVersion
    );
  }

  update(dt: number): void {
    if (!this.active) return;

    // Record trail position (ring buffer)
    if (this.trail.length < TRAIL_LENGTH) {
      this.trail.push({ x: this.x, y: this.y });
    } else {
      this.trail[this.trailIdx] = { x: this.x, y: this.y };
    }
    this.trailIdx = (this.trailIdx + 1) % TRAIL_LENGTH;

    // Homing: track target only if it's still the same logical enemy.
    if (this.hasLiveTarget()) {
      this.targetX = this.target!.x;
      this.targetY = this.target!.y;
    }

    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const moveAmount = this.speed * (dt / 1000);

    if (dist <= moveAmount) {
      this.x = this.targetX;
      this.y = this.targetY;
      this.active = false; // Hit — processed by CombatSystem
    } else {
      this.x += (dx / dist) * moveAmount;
      this.y += (dy / dist) * moveAmount;
    }
  }
}
